import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import {
  BrandsStepSchema,
  BrandsStep,
  PresentationsTabsPath,
  ReportTypeStep,
  ReportTypeStepSchema,
  SelectionsStep,
  SelectionsStepSchema,
  Presentation
} from './types';
import {
  initialBrands,
  initialSelections,
  initialType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useGenerateReport } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/hooks/useGenerateReport';
import { usePresentations } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/hooks/usePresentations';
import { useAppSelector } from 'src/utils/Hooks';
import { getPresentationsQuota } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/service/api';
import { useQuery } from '@tanstack/react-query';

type StepValidity = {
  [PresentationsTabsPath.type]: boolean;
  [PresentationsTabsPath.selections]: boolean;
  [PresentationsTabsPath.brands]: boolean;
  [PresentationsTabsPath.confirmations]: boolean;
};

type PresentationFormContextType = {
  type: UseFormReturn<ReportTypeStep>;
  selections: UseFormReturn<SelectionsStep>;
  brands: UseFormReturn<BrandsStep>;
  isValidStep: (step: PresentationsTabsPath) => boolean;
  stepValidity: StepValidity;
  handleOnSubmit: () => void;
  quota: {
    usage: number;
    limit: number;
  };
  isQuotaLoading: boolean;
  isSubmitting: boolean;
  presentations: Presentation[];
  isPresentationsLoading: boolean;
  presentationsError: Error | null;
};

const PresentationFormContext = createContext<PresentationFormContextType>(null);

const PresentationFormProvider = ({ children }: { children: React.ReactNode }) => {
  const { generateReport, isPending: isSubmitting } = useGenerateReport();
  const defaultCurrency = useAppSelector((state) => state.retailer?.currencyCode || 'USD');

  const { data: quota, isLoading: isQuotaLoading } = useQuery<{ usage: number; limit: number }>({
    queryKey: ['presentationsQuota'],
    queryFn: getPresentationsQuota
  });

  const { data: presentationsData, isLoading: isPresentationsLoading, error: presentationsError } = usePresentations();

  const [stepValidity, setStepValidity] = useState<StepValidity>({
    [PresentationsTabsPath.type]: false,
    [PresentationsTabsPath.selections]: false,
    [PresentationsTabsPath.brands]: true,
    [PresentationsTabsPath.confirmations]: true
  });

  const typeForm = useForm<ReportTypeStep>({
    mode: 'all',
    resolver: zodResolver(ReportTypeStepSchema),
    defaultValues: initialType
  });

  const selectionsForm = useForm<SelectionsStep>({
    mode: 'all',
    resolver: zodResolver(SelectionsStepSchema),
    defaultValues: { ...initialSelections, currency: defaultCurrency }
  });

  const brandsForm = useForm<BrandsStep>({
    mode: 'all',
    resolver: zodResolver(BrandsStepSchema),
    defaultValues: initialBrands
  });

  const isValidStep = useCallback(
    (step: PresentationsTabsPath) => {
      return stepValidity[step];
    },
    [stepValidity]
  );

  const handleOnSubmit = useCallback(() => {
    const data = {
      ...typeForm.getValues(),
      ...selectionsForm.getValues(),
      ...brandsForm.getValues()
    };

    generateReport(data);
  }, [typeForm, selectionsForm, brandsForm, generateReport]);

  useEffect(() => {
    const subscription = typeForm.watch(() => {
      const isValid = ReportTypeStepSchema.safeParse(typeForm.getValues()).success;
      setStepValidity((prev) => ({ ...prev, [PresentationsTabsPath.type]: isValid }));
    });
    return () => subscription.unsubscribe();
  }, [typeForm]);

  useEffect(() => {
    const subscription = selectionsForm.watch(() => {
      const isValid = SelectionsStepSchema.safeParse(selectionsForm.getValues());
      setStepValidity((prev) => ({ ...prev, [PresentationsTabsPath.selections]: isValid.success }));
    });
    return () => subscription.unsubscribe();
  }, [selectionsForm]);

  useEffect(() => {
    const subscription = brandsForm.watch(() => {
      const isValid = BrandsStepSchema.safeParse(brandsForm.getValues()).success;
      setStepValidity((prev) => ({ ...prev, [PresentationsTabsPath.brands]: isValid }));
    });
    return () => subscription.unsubscribe();
  }, [brandsForm]);

  return (
    <PresentationFormContext.Provider
      value={{
        type: typeForm,
        selections: selectionsForm,
        brands: brandsForm,
        isValidStep,
        stepValidity,
        handleOnSubmit,
        quota,
        isQuotaLoading,
        isSubmitting,
        presentations: presentationsData || [],
        isPresentationsLoading,
        presentationsError
      }}
    >
      {children}
    </PresentationFormContext.Provider>
  );
};

export const usePresentationFormContext = () => {
  return useContext(PresentationFormContext);
};

export default PresentationFormProvider;
